import { BaseElementController, ElementController } from "elements/base/controller";
import { BoardContext } from "..";
import { TaskElement } from "./schema";
import { format } from "date-fns";
import { Assignee } from "shared/datamodel/schemas/task-card";
import { equals } from "rambda";
import { TASK_HEIGHT, TASK_HEIGHT_WITH_INFO } from "shared/datamodel/task";
import tracking from "frontend/tracking";

export interface ITaskController extends ElementController<TaskElement> {
  isEditing: () => boolean;
  toggleTag: (tagId: string) => void;
  isTagSelected: (tagId: string) => boolean;
  setDate: (start?: Date, end?: Date) => void;
  getSelectedTags: () => string[];
  setAssignee: (assignee: Assignee | null) => void;
  getAssignee: () => Assignee | null;
  getRequiredHeight: () => number;
  getDateTitle: () => string;
  isPartOfGantt: () => boolean;

  // Analytics
  reportFieldChanged: (field: string, source: "canvas" | "task-modal") => void;
  reportTitleSet: (source: "canvas" | "task-modal") => void;
  reportTaskExpanded: () => void;
}

export default class TaskController extends BaseElementController<TaskElement> implements ITaskController {
  public isEditing(): boolean {
    return this.context.editingElementId === this.id;
  }

  public toggleTag(tagId: string): void {
    const currentTags = this.element.tags ?? [];
    const isCurrentlySelected = currentTags.includes(tagId);

    this.patchElement(this.id, (draft: TaskElement) => {
      if (isCurrentlySelected) {
        draft.tags = currentTags.filter((id) => id !== tagId);
      } else {
        draft.tags = [...currentTags, tagId];
      }
      draft.height = this.calculateRequiredHeight(draft.tags, draft.assignees);
    });
  }

  public isTagSelected(tagId: string): boolean {
    return (this.element.tags ?? []).includes(tagId);
  }

  public getSelectedTags(): string[] {
    return this.element.tags ?? [];
  }

  public setAssignee(assignee: Assignee | null): void {
    this.patchElement(this.id, (draft: TaskElement) => {
      if (assignee) {
        draft.assignees = [assignee];
      } else {
        draft.assignees = [];
      }
      draft.height = this.calculateRequiredHeight(draft.tags, draft.assignees);
    });
  }

  public getAssignee(): Assignee | null {
    const assignees = this.element.assignees ?? [];
    return assignees.length > 0 ? assignees[0] : null;
  }

  public getDateTitle(): string {
    if (!this.element.fromDate) {
      return "Due Date";
    }

    if (this.element.fromDate === this.element.toDate || !this.element.toDate) {
      return format(this.element.fromDate, "dd MMM '‘'yy");
    }

    return `${format(this.element.fromDate, "dd MMM '‘'yy")} - ${format(this.element.toDate, "dd MMM '‘'yy")}`;
  }

  public getRequiredHeight(): number {
    return this.calculateRequiredHeight(this.element.tags, this.element.assignees);
  }

  public isPartOfGantt(): boolean {
    return !!this.element.containerId?.startsWith("gantt");
  }

  public setDate(start?: Date, end?: Date): void {
    this.patchElement(this.id, (task: TaskElement) => {
      task.fromDate = start ? start.getTime() : undefined;
      task.toDate = end ? end.getTime() : undefined;
    });
    this.reportFieldChanged("date", "task-modal");
  }

  public reportFieldChanged(field: string, source: "canvas" | "task-modal"): void {
    tracking.trackAnalyticsEvent("task-edited", {
      from: source,
      field: field,
      isInsideGantt: this.isPartOfGantt(),
    });
  }

  public reportTitleSet(source: "canvas" | "task-modal"): void {
    tracking.trackAnalyticsEvent("task-title-set", {
      from: source,
      isInsideGantt: this.isPartOfGantt(),
    });
  }

  public reportTaskExpanded(): void {
    tracking.trackAnalyticsEvent("task-opened", {
      from: "canvas",
      isInsideGantt: this.isPartOfGantt(),
    });
  }

  getLayoutRect(): { x: number; y: number; width: number; height: number } {
    return { x: this.element.x, y: this.element.y, width: this.element.width, height: this.element.height };
  }

  private calculateRequiredHeight(tags?: string[], assignees?: Assignee[]): number {
    const hasTags = (tags ?? []).length > 0;
    const hasAssignees = (assignees ?? []).length > 0;
    return hasTags || hasAssignees ? TASK_HEIGHT_WITH_INFO : TASK_HEIGHT;
  }

  updateContext(context: BoardContext): void {
    // eslint-disable-next-line unicorn/consistent-function-scoping
    const makeProps = () => {
      return [this.isEditing(), this.context.isReadOnly];
    };

    const oldProps = makeProps();
    super.updateContext(context);
    const newProps = makeProps();

    if (!equals(oldProps, newProps)) {
      this.notify();
    }
  }

  updateElement(element: TaskElement): void {
    // eslint-disable-next-line unicorn/consistent-function-scoping
    const makeElement = () => ({
      // Description and date fields are not included here
      // since they are not displayed on the canvas.

      title: this.element.title,
      description: this.element.description,
      fill: this.element.fill,
      height: this.element.height,
      tags: this.element.tags,
      fromDate: this.element.fromDate,
      toDate: this.element.toDate,
      assignees: this.element.assignees,
      containerId: this.element.containerId,
    });

    const oldElement = makeElement();
    super.updateElement(element);
    const newElement = makeElement();

    if (equals(oldElement, newElement)) {
      return;
    }

    /// Make notifications for changed properties
    const notifications = [
      oldElement.tags !== newElement.tags && "tag-changed",
      oldElement.assignees !== newElement.assignees && "assignee-changed",
      oldElement.fill !== newElement.fill && "fill-changed",
      oldElement.height !== newElement.height && "size-changed",
      oldElement.title !== newElement.title && "title-changed",
      oldElement.description !== newElement.description && "description-changed",
      (oldElement.fromDate !== newElement.fromDate || oldElement.toDate !== newElement.toDate) && "date-changed",
    ].filter(Boolean) as string[];

    this.notify(notifications);
  }
}
